import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type SupportedLanguage = 'pt' | 'en' | 'es' | 'fr' | 'it' | 'ja';

export interface LanguageInfo {
  code: SupportedLanguage;
  name: string;
  nativeName: string;
  flag: string;
}

export const SUPPORTED_LANGUAGES: LanguageInfo[] = [
  { code: 'pt', name: 'Português', nativeName: 'Português (Padrão)', flag: '🇵🇹' },
  { code: 'en', name: 'Inglês', nativeName: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Espanhol', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Francês', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', nativeName: 'Italiano', flag: '🇮🇹' },
  { code: 'ja', name: 'Japonês', nativeName: '日本語', flag: '🇯🇵' },
];

// UI Dictionary for standard interface text
const UI_TRANSLATIONS: Record<SupportedLanguage, Record<string, string>> = {
  pt: {
    'nav.home': 'Início',
    'nav.articles': 'Artigos',
    'nav.community': 'Comunidade',
    'nav.bookstore': 'Livraria',
    'nav.join_community': 'Entre na Comunidade',
    'nav.back': 'Voltar',
    'nav.back_tooltip': 'Voltar para a página anterior',
    'nav.share': 'Compartilhar',
    'footer.tagline': 'Uma comunidade dedicada aos amantes da literatura. Conectando leitores, explorando mundos e compartilhando aventuras.',
    'footer.explore': 'Explorar',
    'footer.articles_reviews': 'Artigos & Resenhas',
    'footer.curated_bookstore': 'Livraria Literafan',
    'footer.community_forum': 'Fórum da Comunidade',
    'footer.categories': 'Categorias',
    'footer.newsletter': 'Newsletter',
    'footer.newsletter_desc': 'Receba as melhores recomendações e novidades literárias semanalmente.',
    'footer.your_email': 'Seu e-mail',
    'footer.subscribe': 'Inscrever-se',
    'footer.rights': 'Todos os direitos reservados.',
    'footer.privacy': 'Privacidade',
    'footer.terms': 'Termos',
    'home.hero_tag': 'Comunidade & Conteúdo Literário',
    'home.hero_title': 'Sua Próxima Aventura Literária Começa Aqui',
    'home.hero_desc': 'Resenhas aprofundadas, análises críticas e uma comunidade vibrante de leitores apaixonados.',
    'home.explore_articles': 'Explorar Artigos',
    'home.visit_bookstore': 'Visitar Livraria',
    'home.featured_articles': 'Artigos em Destaque',
    'home.recommended_books': 'Livros Recomendados',
    'home.reading_clubs': 'Clubes de Leitura',
    'home.community_posts': 'Últimas da Comunidade',
    'articles.title': 'Artigos & Análises',
    'articles.subtitle': 'Explorações profundas sobre literatura, autores e obras marcantes.',
    'articles.search_placeholder': 'Buscar por título, autor ou palavra-chave...',
    'articles.no_found': 'Nenhum artigo encontrado.',
    'article.written_by': 'Escrito por',
    'article.reading_time': 'Tempo de leitura',
    'article.published': 'Publicado',
    'article.min_read': 'min de leitura',
    'article.audio_title': 'Narração da Iara',
    'article.audio_subtitle': 'Ouvir artigo em segundo plano',
    'article.related_topics': 'Tópicos Relacionados',
    'article.share_article': 'Compartilhar',
    'article.comments': 'Comentários',
    'article.add_comment': 'Adicione um comentário...',
    'article.latest_articles': 'Últimos Artigos',
    'article.related_articles': 'Artigos Relacionados',
    'article.not_found': 'Artigo não encontrado',
    'article.back_to_articles': 'Voltar para Artigos',
    'bookstore.title': 'Livraria Literafan',
    'bookstore.subtitle': 'Recomendações especiais selecionadas pela equipe do Literafan.',
    'bookstore.search_placeholder': 'Buscar livros por título, autor ou gênero...',
    'bookstore.buy_amazon': 'Comprar',
    'bookstore.details': 'Sinopse',
    'bookstore.rating': 'Avaliação',
    'community.title': 'Comunidade Literafan',
    'community.subtitle': 'Compartilhe suas leituras, opiniões e produções com outros leitores.',
    'community.create_post': 'Criar Publicação',
    'community.likes': 'Curtidas',
    'translation.badge': 'Tradução automática por IA',
    'translation.showing_original': 'Mostrando original em Português',
    'translation.showing_translated': 'Traduzido automaticamente',
    'translation.see_original': 'Ver original (PT)',
    'translation.see_translation': 'Ver tradução',
    'translation.translating': 'Traduzindo conteúdo...',
    'home.welcome': 'Bem-vindo ao Literafan',
    'home.hero_sub': 'O seu ecossistema literário que te dá a chave para desbravar a imensidão de universos, que só a imaginação humana pode criar.',
    'home.hero_title_1': 'Aventura Literária &',
    'home.hero_title_2': 'Conexão Real',
    'home.discover': 'Descubra',
    'home.weekly_highlights': 'Destaques da Semana',
    'home.see_all_articles': 'Ver todos os artigos',
    'home.most_read': 'Mais Lidos',
    'home.popular_articles': 'Artigos Populares',
    'home.see_all': 'Ver todos',
    'home.our_essence': 'Nossa Essência',
    'home.who_we_are': 'Quem somos?',
    'home.who_we_are_p1': 'Nós, da equipe Literafan, somos um grupo de pessoas apaixonadas pelo fantástico e maravilhoso mundo literário. Pois é nele que podemos viajar até os limites de nossa imaginação, conhecer mundos incríveis e participar de aventuras inesquecíveis no conforto de nosso lar, esquecendo brevemente dos problemas que vêm nos atormentar.',
    'home.who_we_are_p2': 'Por isso, decidimos criar uma comunidade onde todos são aceitos e bem-vindos para participar e compartilhar suas aventuras literárias, fazer novas amizades, descobrir novas paixões, construir relações com pessoas incríveis e, por fim, apresentar a todos, novos universos de histórias e aventuras fascinantes, descritos nas páginas de um simpático e querido livro.',
    'home.our_channel': 'Nosso Canal',
    'home.latest_videos': 'Últimos Vídeos',
    'home.access_channel': 'Acessar Canal',
    'home.special_selection': 'Seleção Especial',
    'home.featured_videos': 'Vídeos Destaques',
    'home.follow_socials': 'Acompanhe nossas redes'
  },
  en: {
    'nav.home': 'Home',
    'nav.articles': 'Articles',
    'nav.community': 'Community',
    'nav.bookstore': 'Bookstore',
    'nav.join_community': 'Join Community',
    'nav.back': 'Back',
    'nav.back_tooltip': 'Go back to previous page',
    'nav.share': 'Share',
    'footer.tagline': 'A community dedicated to literature lovers. Connecting readers, exploring worlds, and sharing adventures.',
    'footer.explore': 'Explore',
    'footer.articles_reviews': 'Articles & Reviews',
    'footer.curated_bookstore': 'Literafan Bookstore',
    'footer.community_forum': 'Community Forum',
    'footer.categories': 'Categories',
    'footer.newsletter': 'Newsletter',
    'footer.newsletter_desc': 'Receive weekly top book recommendations and literary news.',
    'footer.your_email': 'Your email',
    'footer.subscribe': 'Subscribe',
    'footer.rights': 'All rights reserved.',
    'footer.privacy': 'Privacy',
    'footer.terms': 'Terms',
    'home.hero_tag': 'Literary Community & Content',
    'home.hero_title': 'Your Next Literary Adventure Begins Here',
    'home.hero_desc': 'In-depth reviews, critical analyses, and a vibrant community of passionate readers.',
    'home.explore_articles': 'Explore Articles',
    'home.visit_bookstore': 'Visit Bookstore',
    'home.featured_articles': 'Featured Articles',
    'home.recommended_books': 'Recommended Books',
    'home.reading_clubs': 'Reading Clubs',
    'home.community_posts': 'Community Updates',
    'articles.title': 'Articles & Analyses',
    'articles.subtitle': 'Deep explorations into literature, authors, and remarkable works.',
    'articles.search_placeholder': 'Search by title, author, or keyword...',
    'articles.no_found': 'No articles found.',
    'article.written_by': 'Written by',
    'article.reading_time': 'Reading time',
    'article.published': 'Published',
    'article.min_read': 'min read',
    'article.audio_title': 'Iara Narration',
    'article.audio_subtitle': 'Listen to article in background',
    'article.related_topics': 'Related Topics',
    'article.share_article': 'Share',
    'article.comments': 'Comments',
    'article.add_comment': 'Add a comment...',
    'article.latest_articles': 'Latest Articles',
    'article.related_articles': 'Related Articles',
    'article.not_found': 'Article not found',
    'article.back_to_articles': 'Back to Articles',
    'bookstore.title': 'Literafan Bookstore',
    'bookstore.subtitle': 'Special recommendations hand-selected by the Literafan team.',
    'bookstore.search_placeholder': 'Search books by title, author, or genre...',
    'bookstore.buy_amazon': 'Buy',
    'bookstore.details': 'Synopsis',
    'bookstore.rating': 'Rating',
    'community.title': 'Literafan Community',
    'community.subtitle': 'Share your readings, thoughts, and creations with fellow readers.',
    'community.create_post': 'Create Post',
    'community.likes': 'Likes',
    'translation.badge': 'AI Automatic Translation',
    'translation.showing_original': 'Showing original Portuguese',
    'translation.showing_translated': 'Automatically translated',
    'translation.see_original': 'View original (PT)',
    'translation.see_translation': 'View translation',
    'translation.translating': 'Translating content...',
    'home.welcome': 'Welcome to Literafan',
    'home.hero_sub': 'Your literary ecosystem giving you the key to explore the vastness of universes that only human imagination can create.',
    'home.hero_title_1': 'Literary Adventure &',
    'home.hero_title_2': 'Real Connection',
    'home.discover': 'Discover',
    'home.weekly_highlights': 'Weekly Highlights',
    'home.see_all_articles': 'See all articles',
    'home.most_read': 'Most Read',
    'home.popular_articles': 'Popular Articles',
    'home.see_all': 'See all',
    'home.our_essence': 'Our Essence',
    'home.who_we_are': 'Who are we?',
    'home.who_we_are_p1': 'We at the Literafan team are a group of people passionate about the fantastic world of literature. It allows us to travel to the edges of our imagination, explore incredible worlds, and take part in unforgettable adventures from the comfort of home.',
    'home.who_we_are_p2': 'That is why we decided to create a community where everyone is welcome to participate and share their literary journeys, make new friends, discover new passions, build connections, and experience fascinating stories.',
    'home.our_channel': 'Our Channel',
    'home.latest_videos': 'Latest Videos',
    'home.access_channel': 'Visit Channel',
    'home.special_selection': 'Special Selection',
    'home.featured_videos': 'Featured Videos',
    'home.follow_socials': 'Follow our socials'
  },
  es: {
    'nav.home': 'Inicio',
    'nav.articles': 'Artículos',
    'nav.community': 'Comunidad',
    'nav.bookstore': 'Librería',
    'nav.join_community': 'Únete a la Comunidad',
    'nav.back': 'Volver',
    'nav.back_tooltip': 'Volver a la página anterior',
    'nav.share': 'Compartir',
    'footer.tagline': 'Una comunidad dedicada a los amantes de la literatura. Conectando lectores, explorando mundos y compartiendo aventuras.',
    'footer.explore': 'Explorar',
    'footer.articles_reviews': 'Artículos y Reseñas',
    'footer.curated_bookstore': 'Librería Literafan',
    'footer.community_forum': 'Foro de la Comunidad',
    'footer.categories': 'Categorías',
    'footer.newsletter': 'Boletín',
    'footer.newsletter_desc': 'Recibe semanalmente las mejores recomendaciones e innovaciones literarias.',
    'footer.your_email': 'Tu correo electrónico',
    'footer.subscribe': 'Suscribirse',
    'footer.rights': 'Todos los derechos reservados.',
    'footer.privacy': 'Privacidad',
    'footer.terms': 'Términos',
    'home.hero_tag': 'Comunidad y Contenido Literario',
    'home.hero_title': 'Tu Próxima Aventura Literaria Empieza Aquí',
    'home.hero_desc': 'Reseñas profundas, análisis críticos y una comunidad vibrante de lectores apasionados.',
    'home.explore_articles': 'Explorar Artículos',
    'home.visit_bookstore': 'Visitar Librería',
    'home.featured_articles': 'Artículos Destacados',
    'home.recommended_books': 'Libros Recomendados',
    'home.reading_clubs': 'Clubes de Lectura',
    'home.community_posts': 'Novedades de la Comunidad',
    'articles.title': 'Artículos y Análisis',
    'articles.subtitle': 'Exploraciones profundas sobre literatura, autores y obras destacadas.',
    'articles.search_placeholder': 'Buscar por título, autor o palabra clave...',
    'articles.no_found': 'No se encontraron artículos.',
    'article.written_by': 'Escrito por',
    'article.reading_time': 'Tiempo de lectura',
    'article.published': 'Publicado',
    'article.min_read': 'min de lectura',
    'article.audio_title': 'Narración de Iara',
    'article.audio_subtitle': 'Escuchar artículo en segundo plano',
    'article.related_topics': 'Temas Relacionados',
    'article.share_article': 'Compartir',
    'article.comments': 'Comentarios',
    'article.add_comment': 'Añadir un comentario...',
    'article.latest_articles': 'Últimos Artículos',
    'article.related_articles': 'Artículos Relacionados',
    'article.not_found': 'Artículo no encontrado',
    'article.back_to_articles': 'Volver a Artículos',
    'bookstore.title': 'Librería Literafan',
    'bookstore.subtitle': 'Recomendaciones especiales seleccionadas por el equipo de Literafan.',
    'bookstore.search_placeholder': 'Buscar libros por título, autor o género...',
    'bookstore.buy_amazon': 'Comprar',
    'bookstore.details': 'Sinopsis',
    'bookstore.rating': 'Valoración',
    'community.title': 'Comunidad Literafan',
    'community.subtitle': 'Comparte tus lecturas, opiniones y creaciones con otros lectores.',
    'community.create_post': 'Crear Publicación',
    'community.likes': 'Me gusta',
    'translation.badge': 'Traducción automática por IA',
    'translation.showing_original': 'Mostrando original en Portugués',
    'translation.showing_translated': 'Traducido automáticamente',
    'translation.see_original': 'Ver original (PT)',
    'translation.see_translation': 'Ver traducción',
    'translation.translating': 'Traduciendo contenido...',
    'home.welcome': 'Bienvenido a Literafan',
    'home.hero_sub': 'Tu ecosistema literario que te da la llave para explorar la inmensidad de universos que solo la imaginación humana puede crear.',
    'home.hero_title_1': 'Aventura Literaria y',
    'home.hero_title_2': 'Conexión Real',
    'home.discover': 'Descubre',
    'home.weekly_highlights': 'Destacados de la Semana',
    'home.see_all_articles': 'Ver todos los artículos',
    'home.most_read': 'Más Leídos',
    'home.popular_articles': 'Artículos Populares',
    'home.see_all': 'Ver todos',
    'home.our_essence': 'Nuestra Esencia',
    'home.who_we_are': '¿Quiénes somos?',
    'home.who_we_are_p1': 'En el equipo de Literafan somos un grupo de apasionados por el fantástico mundo literario. En él viajamos hasta los límites de nuestra imaginación y vivimos aventuras inolvidables.',
    'home.who_we_are_p2': 'Por ello, creamos una comunidad abierta donde todos son bienvenidos a compartir sus lecturas, hacer amigos y descubrir apasionantes historias.',
    'home.our_channel': 'Nuestro Canal',
    'home.latest_videos': 'Últimos Videos',
    'home.access_channel': 'Ir al Canal',
    'home.special_selection': 'Selección Especial',
    'home.featured_videos': 'Videos Destacados',
    'home.follow_socials': 'Sigue nuestras redes'
  },
  fr: {
    'nav.home': 'Accueil',
    'nav.articles': 'Articles',
    'nav.community': 'Communauté',
    'nav.bookstore': 'Librairie',
    'nav.join_community': 'Rejoindre la Communauté',
    'nav.back': 'Retour',
    'nav.back_tooltip': 'Retourner à la page précédente',
    'nav.share': 'Partager',
    'footer.tagline': 'Une communauté dédiée aux passionnés de littérature. Connecter les lecteurs, explorer des mondes et partager des aventures.',
    'footer.explore': 'Explorer',
    'footer.articles_reviews': 'Articles & Critiques',
    'footer.curated_bookstore': 'Librairie Literafan',
    'footer.community_forum': 'Forum de la Communauté',
    'footer.categories': 'Catégories',
    'footer.newsletter': 'Newsletter',
    'footer.newsletter_desc': 'Recevez chaque semaine nos meilleures recommandations et actualités littéraires.',
    'footer.your_email': 'Votre e-mail',
    'footer.subscribe': "S'abonner",
    'footer.rights': 'Tous droits réservés.',
    'footer.privacy': 'Confidentialité',
    'footer.terms': 'Conditions',
    'home.hero_tag': 'Communauté & Contenu Littéraire',
    'home.hero_title': 'Votre Prochaine Aventure Littéraire Commence Ici',
    'home.hero_desc': 'Des critiques approfondies, des analyses littéraires et une communauté vibrante de lecteurs passionnés.',
    'home.explore_articles': 'Explorer les Articles',
    'home.visit_bookstore': 'Visiter la Librairie',
    'home.featured_articles': 'Articles à la Une',
    'home.recommended_books': 'Livres Recommandés',
    'home.reading_clubs': 'Clubs de Lecture',
    'home.community_posts': 'Dernières de la Communauté',
    'articles.title': 'Articles & Analyses',
    'articles.subtitle': 'Explorations profondes de la littérature, des auteurs et des œuvres marquantes.',
    'articles.search_placeholder': 'Rechercher par titre, auteur ou mot-clé...',
    'articles.no_found': 'Aucun article trouvé.',
    'article.written_by': 'Écrit par',
    'article.reading_time': 'Temps de lecture',
    'article.published': 'Publié',
    'article.min_read': 'min de lecture',
    'article.audio_title': 'Narration par Iara',
    'article.audio_subtitle': "Écouter l'article en arrière-plan",
    'article.related_topics': 'Sujets Associés',
    'article.share_article': 'Partager',
    'article.comments': 'Commentaires',
    'article.add_comment': 'Ajouter un commentaire...',
    'article.latest_articles': 'Derniers Articles',
    'article.related_articles': 'Articles Similaires',
    'article.not_found': 'Article non trouvé',
    'article.back_to_articles': 'Retour aux Articles',
    'bookstore.title': 'Librairie Literafan',
    'bookstore.subtitle': "Recommandations spéciales sélectionnées par l'équipe Literafan.",
    'bookstore.search_placeholder': 'Rechercher par titre, auteur ou genre...',
    'bookstore.buy_amazon': 'Acheter',
    'bookstore.details': 'Synopsis',
    'bookstore.rating': 'Évaluation',
    'community.title': 'Communauté Literafan',
    'community.subtitle': 'Partagez vos lectures, vos avis et vos créations avec dautres lecteurs.',
    'community.create_post': 'Créer une Publication',
    'community.likes': "J'aime",
    'translation.badge': 'Traduction automatique par IA',
    'translation.showing_original': 'Affichage de loriginal en Portugais',
    'translation.showing_translated': 'Traduit automatiquement',
    'translation.see_original': 'Voir original (PT)',
    'translation.see_translation': 'Voir la traduction',
    'translation.translating': 'Traduction du contenu...',
    'home.welcome': 'Bienvenue sur Literafan',
    'home.hero_sub': 'Votre écosystème littéraire pour explorer l\'immensité des univers nés de l\'imagination humaine.',
    'home.hero_title_1': 'Aventure Littéraire &',
    'home.hero_title_2': 'Véritable Connexion',
    'home.discover': 'Découvrir',
    'home.weekly_highlights': 'Temps Forts de la Semaine',
    'home.see_all_articles': 'Voir tous les articles',
    'home.most_read': 'Les Plus Lus',
    'home.popular_articles': 'Articles Populaires',
    'home.see_all': 'Voir tout',
    'home.our_essence': 'Notre Essence',
    'home.who_we_are': 'Qui sommes-nous ?',
    'home.who_we_are_p1': 'L\'équipe de Literafan est passionnée par le monde de la littérature. Elle nous permet de voyager au bout de notre imagination.',
    'home.who_we_are_p2': 'Nous avons créé cette communauté pour partager nos lectures, tisser des liens et découvrir de nouvelles histoires fascinantes.',
    'home.our_channel': 'Notre Chaîne',
    'home.latest_videos': 'Dernières Vidéos',
    'home.access_channel': 'Accéder à la Chaîne',
    'home.special_selection': 'Sélection Spéciale',
    'home.featured_videos': 'Vidéos à la Une',
    'home.follow_socials': 'Suivez nos réseaux'
  },
  it: {
    'nav.home': 'Home',
    'nav.articles': 'Articoli',
    'nav.community': 'Community',
    'nav.bookstore': 'Libreria',
    'nav.join_community': 'Unisciti alla Community',
    'nav.back': 'Indietro',
    'nav.back_tooltip': 'Torna alla pagina precedente',
    'nav.share': 'Condividi',
    'footer.tagline': 'Una community dedicata agli amanti della letteratura. Connettere lettori, esplorare mondi e condividere avventure.',
    'footer.explore': 'Esplora',
    'footer.articles_reviews': 'Articoli & Recensioni',
    'footer.curated_bookstore': 'Libreria Literafan',
    'footer.community_forum': 'Forum della Community',
    'footer.categories': 'Categorie',
    'footer.newsletter': 'Newsletter',
    'footer.newsletter_desc': 'Ricevi ogni settimana i migliori consigli e le novità letterarie.',
    'footer.your_email': 'La tua email',
    'footer.subscribe': 'Iscriviti',
    'footer.rights': 'Tutti i diritti riservati.',
    'footer.privacy': 'Privacy',
    'footer.terms': 'Termini',
    'home.hero_tag': 'Community & Contenuto Letterario',
    'home.hero_title': 'La Tua Prossima Avventura Letteraria Inizia Qui',
    'home.hero_desc': 'Recensioni approfondite, analisi critiche e una vivace community di lettori appassionati.',
    'home.explore_articles': 'Esplora Articoli',
    'home.visit_bookstore': 'Visita la Libreria',
    'home.featured_articles': 'Articoli in Evidenza',
    'home.recommended_books': 'Libri Consigliati',
    'home.reading_clubs': 'Club di Lettura',
    'home.community_posts': 'Ultime dalla Community',
    'articles.title': 'Articoli & Analisi',
    'articles.subtitle': 'Esplorazioni profonde su letteratura, autori e opere straordinarie.',
    'articles.search_placeholder': 'Cerca per titolo, autore o parola chiave...',
    'articles.no_found': 'Nessun articolo trovato.',
    'article.written_by': 'Scritto da',
    'article.reading_time': 'Tempo di lettura',
    'article.published': 'Pubblicato',
    'article.min_read': 'min di lettura',
    'article.audio_title': 'Narrazione di Iara',
    'article.audio_subtitle': "Ascolta l'articolo in background",
    'article.related_topics': 'Argomenti Correlati',
    'article.share_article': 'Condividi',
    'article.comments': 'Commenti',
    'article.add_comment': 'Aggiungi un commento...',
    'article.latest_articles': 'Ultimi Articoli',
    'article.related_articles': 'Articoli Correlati',
    'article.not_found': 'Articolo non trovato',
    'article.back_to_articles': 'Torna agli Articoli',
    'bookstore.title': 'Libreria Literafan',
    'bookstore.subtitle': 'Raccomandazioni speciali selezionate dal team di Literafan.',
    'bookstore.search_placeholder': 'Cerca libri per titolo, autore o genere...',
    'bookstore.buy_amazon': 'Acquista',
    'bookstore.details': 'Sinossi',
    'bookstore.rating': 'Valutazione',
    'community.title': 'Community Literafan',
    'community.subtitle': 'Condividi le tue letture, opinioni e creazioni con altri lettori.',
    'community.create_post': 'Crea Post',
    'community.likes': 'Mi piace',
    'translation.badge': 'Traduzione automatica AI',
    'translation.showing_original': 'Mostra originale in Portoghese',
    'translation.showing_translated': 'Tradotto automaticamente',
    'translation.see_original': 'Vedi originale (PT)',
    'translation.see_translation': 'Vedi traduzione',
    'translation.translating': 'Traduzione del contenuto...',
    'home.welcome': 'Benvenuto su Literafan',
    'home.hero_sub': 'Il tuo ecosistema letterario per esplorare l\'immensità degli universi creati dall\'immaginazione umana.',
    'home.hero_title_1': 'Avventura Letteraria &',
    'home.hero_title_2': 'Connessione Reale',
    'home.discover': 'Scopri',
    'home.weekly_highlights': 'In Evidenza della Settimana',
    'home.see_all_articles': 'Vedi tutti gli artigos',
    'home.most_read': 'I Più Letti',
    'home.popular_articles': 'Articoli Popolari',
    'home.see_all': 'Vedi tutti',
    'home.our_essence': 'La Nostra Essenza',
    'home.who_we_are': 'Chi siamo?',
    'home.who_we_are_p1': 'Noi del team Literafan siamo appassionati del mondo della letteratura, che ci permette di viaggiare ai confini della nostra immaginazione.',
    'home.who_we_are_p2': 'Abbiamo creato una community per condividere le nostre letture, fare nuove amicizie e scoprire storie affascinanti.',
    'home.our_channel': 'Il Nostro Canale',
    'home.latest_videos': 'Ultimi Video',
    'home.access_channel': 'Accedi al Canale',
    'home.special_selection': 'Selezione Speciale',
    'home.featured_videos': 'Video in Evidenza',
    'home.follow_socials': 'Segui i nostri social'
  },
  ja: {
    'nav.home': 'ホーム',
    'nav.articles': '記事',
    'nav.community': 'コミュニティ',
    'nav.bookstore': '書店',
    'nav.join_community': 'コミュニティに参加',
    'nav.back': '戻る',
    'nav.back_tooltip': '前のページに戻る',
    'nav.share': '共有',
    'footer.tagline': '文学愛好家のためのコミュニティ。読者をつなぎ、世界を探求し、冒険を共有します。',
    'footer.explore': '探索',
    'footer.articles_reviews': '記事 & 書評',
    'footer.curated_bookstore': 'Literafan書店',
    'footer.community_forum': 'コミュニティフォーラム',
    'footer.categories': 'カテゴリー',
    'footer.newsletter': 'ニュースレター',
    'footer.newsletter_desc': '毎週、最高のおすすめ本と文学ニュースをお届けします。',
    'footer.your_email': 'メールアドレス',
    'footer.subscribe': '登録する',
    'footer.rights': 'All rights reserved.',
    'footer.privacy': 'プライバシー',
    'footer.terms': '利用規約',
    'home.hero_tag': '文学コミュニティ & コンテンツ',
    'home.hero_title': 'あなたの次の文学の冒険がここから始まります',
    'home.hero_desc': '深掘りした書評、批評的分析、そして情熱的な読者による活気あるコミュニティ。',
    'home.explore_articles': '記事を見る',
    'home.visit_bookstore': '書店を訪れる',
    'home.featured_articles': '注目の記事',
    'home.recommended_books': 'おすすめの本',
    'home.reading_clubs': '読書クラブ',
    'home.community_posts': 'コミュニティの投稿',
    'articles.title': '記事 & 分析',
    'articles.subtitle': '文学、著者、名作についての深い探求。',
    'articles.search_placeholder': 'タイトル、著者、キーワードで検索...',
    'articles.no_found': '記事が見つかりませんでした。',
    'article.written_by': '著者',
    'article.reading_time': '読書時間',
    'article.published': '公開日',
    'article.min_read': '分で読める',
    'article.audio_title': 'イアラによる朗読',
    'article.audio_subtitle': 'バックグラウンドで記事を聞く',
    'article.related_topics': '関連トピック',
    'article.share_article': '記事を共有',
    'article.comments': 'コメント',
    'article.add_comment': 'コメントを追加...',
    'article.latest_articles': '最新の記事',
    'article.related_articles': '関連記事',
    'article.not_found': '記事が見つかりません',
    'article.back_to_articles': '記事一覧に戻る',
    'bookstore.title': 'Literafan書店',
    'bookstore.subtitle': 'Literafanチームが特別に厳選したおすすめ作品。',
    'bookstore.search_placeholder': 'タイトル、著者、ジャンルで本を検索...',
    'bookstore.buy_amazon': '購入',
    'bookstore.details': 'あらすじ',
    'bookstore.rating': '評価',
    'community.title': 'Literafanコミュニティ',
    'community.subtitle': '読書体験や意見、作品を他の読者と共有しましょう。',
    'community.create_post': '投稿を作成',
    'community.likes': 'いいね',
    'translation.badge': 'AI自動翻訳',
    'translation.showing_original': 'ポルトガル語原文を表示中',
    'translation.showing_translated': '自動翻訳済み',
    'translation.see_original': '原文を見る (PT)',
    'translation.see_translation': '翻訳を見る',
    'translation.translating': 'コンテンツを翻訳中...',
    'home.welcome': 'Literafanへようこそ',
    'home.hero_sub': '人間の想像力が生み出す広大な宇宙を探求するための文学エコシステム。',
    'home.hero_title_1': '文学の冒険 &',
    'home.hero_title_2': '本物のつながり',
    'home.discover': '発見する',
    'home.weekly_highlights': '今週のハイライト',
    'home.see_all_articles': 'すべての記事を見る',
    'home.most_read': '最も読まれている記事',
    'home.popular_articles': '人気記事',
    'home.see_all': 'すべて見る',
    'home.our_essence': '私たちの本質',
    'home.who_we_are': '私達について',
    'home.who_we_are_p1': 'Literafanチームは、素晴らしい文学の世界に情熱を注ぐグループです。',
    'home.who_we_are_p2': '読書体験を共有し、新しい友人を作り、魅惑的な物語を発見できるコミュニティを創設しました。',
    'home.our_channel': '公式チャンネル',
    'home.latest_videos': '最新動画',
    'home.access_channel': 'チャンネルを見る',
    'home.special_selection': '特別セレクト',
    'home.featured_videos': '注目動画',
    'home.follow_socials': 'SNSをフォロー'
  }
};

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: (key: string, defaultText?: string) => string;
  translateDynamicText: (text: string, isHtml?: boolean) => Promise<string>;
  currentLangInfo: LanguageInfo;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<SupportedLanguage>('pt');

  const setLanguage = (lang: SupportedLanguage) => {
    setLanguageState(lang);
  };

  const currentLangInfo = SUPPORTED_LANGUAGES[0];

  const t = (key: string, defaultText?: string): string => {
    return UI_TRANSLATIONS.pt[key] || defaultText || key;
  };

  const translateDynamicText = async (text: string): Promise<string> => {
    return text || '';
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, translateDynamicText, currentLangInfo }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const useTranslatedText = (text?: string) => {
  return text || '';
};

export const TranslatedText: React.FC<{
  text?: string;
  isHtml?: boolean;
  className?: string;
  as?: any;
}> = ({ text = '', isHtml = false, className = '', as: Component = 'span' }) => {
  if (isHtml) {
    return (
      <Component 
        className={className} 
        dangerouslySetInnerHTML={{ __html: text }} 
      />
    );
  }

  return <Component className={className}>{text}</Component>;
};
